import { usePermissions } from '@/contexts/PermissionContext';

/**
 * Conditionally renders children based on module-level permissions.
 * Usage: <PermissionGate module="HSE" action="create"><Button>Add Incident</Button></PermissionGate>
 *
 * @param {string} module - Module code (e.g., 'HSE', 'RAID', 'COST_CONTROL')
 * @param {string} action - Required action ('view', 'create', 'edit', 'delete', 'approve', 'export', 'import')
 * @param {React.ReactNode} fallback - Optional fallback to render when access denied
 */
export function PermissionGate({ module, action = 'view', fallback = null, children }) {
  const { can } = usePermissions();

  if (!can(module, action)) {
    return fallback;
  }

  return children;
}

/**
 * Hook for inline permission checks.
 * Usage: const { canCreateHSE, canEditRAID } = useModulePermissions('HSE', 'RAID');
 */
export function useModulePermissions(...moduleCodes) {
  const { can } = usePermissions();
  const result = {};
  for (const mc of moduleCodes) {
    const name = mc.replace(/_/g, '');
    result[`canView${name}`] = can(mc, 'view');
    result[`canCreate${name}`] = can(mc, 'create');
    result[`canEdit${name}`] = can(mc, 'edit');
    result[`canDelete${name}`] = can(mc, 'delete');
    result[`canApprove${name}`] = can(mc, 'approve');
    result[`canExport${name}`] = can(mc, 'export');
    result[`canImport${name}`] = can(mc, 'import');
  }
  return result;
}

export default PermissionGate;
